/**
 * Choosing contact with the world by structural value.
 *
 * A model may propose future branches, observations, and protocols. It cannot
 * attach a numeric value that moves a test toward recommendation. The actionable
 * record below is derived from exact future/criterion/work references and the
 * user's own effort, elapsed-time, and money figures.
 */

import type { ReasonNode, Session } from './types'
import { reasoningKernelOf } from './types'
import {
  DEFAULT_TEST_WEIGHTS,
  executableDiscriminatingTest,
  normalizeTest,
  testScore,
  withUserTestCost,
  type DiscriminatingTest,
  type FutureBearing,
  type TestWeights,
  type UserTestCost,
} from './contradiction'

export type DecisionDisposition = 'advance' | 'reject' | 'defer' | 'unchanged' | 'unknown'

export interface FalsificationCriterion {
  /** Stable, content-derived id supplied to the proposing model. */
  id: string
  text: string
}

export interface FalsificationDecisionEffect {
  criterionId: string
  disposition: DecisionDisposition
}

export interface FalsificationWorkItem {
  /** Stable portfolio-local id shared by every future that preserves this work. */
  id: string
  label: string
}

export interface FalsificationFuture {
  /** Missing only on legacy records, which can never recommend a test. */
  id?: string
  bearing: FutureBearing
  title: string
  /** Claims prepared before the result, not yet installed on the graph. */
  claims: Array<{ label: string; claim: string; reasoning: string }>
  /** Narrative retained for display; deterministic rules do not read it. */
  decisionEffect: string
  /** Legacy display labels. New records also carry exact `usefulWorkIds`. */
  usefulWork: string[]
  /** Exact criterion dispositions. Missing only on legacy records. */
  decisionEffects?: FalsificationDecisionEffect[]
  /** Exact references into `FalsificationPortfolio.workItems`. */
  usefulWorkIds?: string[]
}

export interface FalsificationOutcomeDerivation {
  bearing: FutureBearing
  compatibleFutureIds: string[]
  eliminatedFutureIds: string[]
  separatedFuturePairs: number
  resolvedCriteria: Array<{ criterionId: string; disposition: DecisionDisposition }>
  reusableWorkIds: string[]
}

export type FalsificationDerivationStatus = 'derived' | 'legacy-inert' | 'invalid'

export interface FalsificationTestDerivation {
  version: 1
  testId: string
  status: FalsificationDerivationStatus
  /** Named, deterministic rules or exact validation failures. */
  rules: string[]
  futureCount: number
  totalFuturePairs: number
  outcomes: FalsificationOutcomeDerivation[]
  worstCaseFuturesEliminated: number
  meanPairEliminations: number
  worstCaseDecisionCriteriaResolved: number
  decisionCriteriaChanged: string[]
  worstCaseReusableWork: number
  reusableWorkUniverse: number
  worstCaseReusableWorkRatio: number
}

export type FalsificationRecommendationStatus =
  | 'recommended'
  | 'no-tests'
  | 'legacy-inert'
  | 'invalid-structure'
  | 'needs-user-prices'
  | 'no-unique-dominator'

export interface FalsificationRecommendationReceipt {
  version: 1
  rule: 'falsification.recommendation.unique-pareto-dominator.v1'
  status: FalsificationRecommendationStatus
  consideredTestIds: string[]
  /** Exact axes used; there is no hidden weighted sum. */
  benefitAxes: readonly [
    'worstCaseFuturesEliminated',
    'meanPairEliminations',
    'worstCaseDecisionCriteriaResolved',
    'decisionCriteriaChanged',
    'worstCaseReusableWork',
  ]
  costAxes: readonly ['effortHours', 'elapsedDays', 'moneyUsd']
  winnerId?: string
}

export interface FalsificationPortfolio {
  id: string
  targetId: string
  targetClaim: string
  createdAt: number
  opId?: string
  model?: { provider: string; id: string; name: string }
  tests: DiscriminatingTest[]
  futures: FalsificationFuture[]
  /** Missing only on legacy records. An empty array is a valid no-criterion target. */
  criteria?: FalsificationCriterion[]
  /** Missing only on legacy records. An empty array truthfully means no reusable work. */
  workItems?: FalsificationWorkItem[]
  derivations: FalsificationTestDerivation[]
  recommendation: FalsificationRecommendationReceipt
  recommendedTestId?: string
  structuralValue: {
    downstreamClaims: number
    decisionCriteria: number
    score: number
  }
  resolved?: {
    bearing: FutureBearing
    outcomeRecordedAt: number
    activeFuture: FalsificationFuture
    inactive: FutureBearing[]
  }
}

export interface FalsificationCandidate {
  node: ReasonNode
  downstreamClaims: number
  decisionCriteria: number
  criteria: FalsificationCriterion[]
  /** Pure structural ordering. Higher is worked first. */
  score: number
}

const compareStableId = (left: string, right: string): number =>
  left < right ? -1 : left > right ? 1 : 0

const childrenOf = (session: Session): Map<string, string[]> => {
  const out = new Map<string, string[]>()
  for (const id of session.order) {
    const node = session.nodes[id]
    if (!node) continue
    const parents = new Set<string>([
      ...(node.parentId ? [node.parentId] : []),
      ...(node.bridgeFrom ?? []),
    ])
    for (const parent of parents) {
      const list = out.get(parent)
      if (list) list.push(id)
      else out.set(parent, [id])
    }
  }
  return out
}

const descendantCount = (children: Map<string, string[]>, start: string): number => {
  const seen = new Set<string>()
  const stack = [...(children.get(start) ?? [])]
  while (stack.length) {
    const id = stack.pop()!
    if (seen.has(id)) continue
    seen.add(id)
    for (const child of children.get(id) ?? []) stack.push(child)
  }
  return seen.size
}

const DECISION_STOP_WORDS = new Set([
  'a',
  'an',
  'and',
  'are',
  'as',
  'at',
  'be',
  'by',
  'for',
  'from',
  'if',
  'in',
  'is',
  'it',
  'of',
  'on',
  'only',
  'or',
  'that',
  'the',
  'this',
  'to',
  'when',
  'with',
])

const decisionTokens = (value: string): Set<string> =>
  new Set(
    value
      .normalize('NFKC')
      .toLocaleLowerCase('en-US')
      .match(/[\p{L}\p{N}]+/gu)
      ?.filter((token) => token.length > 2 && !DECISION_STOP_WORDS.has(token)) ?? [],
  )

// The kernel through `reasoningKernelOf`: an argument written for words the
// reader has since replaced must not tie their claim to a criterion.
const nodeText = (node: ReasonNode): string =>
  [node.label, node.claim, reasoningKernelOf(node), ...node.evidence, ...node.nextSteps].join(' ')

const stableTextHash = (value: string): string => {
  let hash = 0x811c9dc5
  for (let index = 0; index < value.length; index++) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(36).padStart(7, '0')
}

/** Stable across unrelated brief insertions and independent of locale collation. */
export function falsificationCriterionId(text: string, duplicateOrdinal = 1): string {
  const normalized = [...decisionTokens(text)].sort(compareStableId).join(' ')
  return `criterion:${stableTextHash(normalized)}:${duplicateOrdinal}`
}

/** Future ids are fixed before the model sees the task and must be copied exactly. */
export function falsificationFutureId(portfolioId: string, bearing: FutureBearing): string {
  return `${portfolioId}:future:${bearing}`
}

/**
 * Find only criteria with a lexical anchor in this claim's dependency cone.
 *
 * The brief is global, but a global count attached to every node would pretend
 * every decision depends on every claim. This conservative join can miss a
 * synonym; it cannot manufacture a dependency absent from the record.
 */
function dependentDecisionCriteria(
  session: Session,
  children: ReadonlyMap<string, string[]>,
  start: string,
): FalsificationCriterion[] {
  const cone = new Set<string>([start])
  const stack = [...(children.get(start) ?? [])]
  while (stack.length) {
    const id = stack.pop()!
    if (cone.has(id)) continue
    cone.add(id)
    for (const child of children.get(id) ?? []) stack.push(child)
  }
  const claimTokens = new Set<string>()
  for (const id of cone) {
    const node = session.nodes[id]
    if (!node) continue
    for (const token of decisionTokens(nodeText(node))) claimTokens.add(token)
  }
  const duplicateCounts = new Map<string, number>()
  return (session.brief ?? []).flatMap((directive) => {
    if (directive.kind !== 'criterion') return []
    const criterionTokens = decisionTokens(directive.text)
    if (![...criterionTokens].some((token) => claimTokens.has(token))) return []
    const key = [...criterionTokens].sort(compareStableId).join(' ')
    const ordinal = (duplicateCounts.get(key) ?? 0) + 1
    duplicateCounts.set(key, ordinal)
    return [{ id: falsificationCriterionId(directive.text, ordinal), text: directive.text.trim() }]
  })
}

/** Every unresolved claim ordered by what would have to be reconsidered below it. */
export function falsificationCandidates(session: Session): FalsificationCandidate[] {
  const children = childrenOf(session)
  return session.order
    .flatMap((id) => {
      const node = session.nodes[id]
      if (!node || node.terminal || node.outcome) return []
      if (node.parentId === null && session.frame && session.frame !== 'claim') return []
      const downstreamClaims = descendantCount(children, id)
      const criteria = dependentDecisionCriteria(session, children, id)
      return [
        {
          node,
          downstreamClaims,
          decisionCriteria: criteria.length,
          criteria,
          score: 1 + downstreamClaims + criteria.length * 2,
        },
      ]
    })
    .sort(
      (a, b) =>
        b.score - a.score ||
        (a.node.createdOrdinal ?? Number.MAX_SAFE_INTEGER) -
          (b.node.createdOrdinal ?? Number.MAX_SAFE_INTEGER) ||
        compareStableId(a.node.id, b.node.id),
    )
}

const FUTURES: readonly FutureBearing[] = ['positive', 'negative', 'inconclusive']

export function completeFutures(futures: readonly FalsificationFuture[]): {
  futures: FalsificationFuture[]
  missing: FutureBearing[]
} {
  const first = new Map<FutureBearing, FalsificationFuture>()
  for (const future of futures) if (!first.has(future.bearing)) first.set(future.bearing, future)
  return {
    futures: FUTURES.flatMap((bearing) => {
      const future = first.get(bearing)
      return future ? [future] : []
    }),
    missing: FUTURES.filter((bearing) => !first.has(bearing)),
  }
}

/** @deprecated Weighted portfolio scores are not actionable. Kept for UI compatibility. */
export interface PortfolioWeights extends TestWeights {
  downstream: number
  criterion: number
}

/** @deprecated Only the cost weights remain effective. */
export const DEFAULT_PORTFOLIO_WEIGHTS: PortfolioWeights = {
  ...DEFAULT_TEST_WEIGHTS,
  downstream: 0,
  criterion: 0,
}

/**
 * Compatibility affordability display. Structural load is common to every test
 * in a portfolio and model-authored value floats are deliberately ignored.
 */
export function portfolioTestScore(
  test: DiscriminatingTest,
  _structural: FalsificationPortfolio['structuralValue'],
  weights: PortfolioWeights = DEFAULT_PORTFOLIO_WEIGHTS,
): number {
  return testScore(test, weights)
}

type PortfolioStructure = Pick<
  FalsificationPortfolio,
  'id' | 'targetId' | 'tests' | 'futures' | 'criteria' | 'workItems'
>

const sortedUnique = (values: readonly string[]): string[] =>
  [...new Set(values)].sort(compareStableId)

const duplicateIds = (values: readonly string[]): string[] => {
  const seen = new Set<string>()
  const duplicates = new Set<string>()
  for (const value of values) {
    if (seen.has(value)) duplicates.add(value)
    seen.add(value)
  }
  return [...duplicates].sort(compareStableId)
}

const hasStructuredIdentifiers = (portfolio: PortfolioStructure): boolean =>
  portfolio.criteria !== undefined ||
  portfolio.workItems !== undefined ||
  portfolio.futures.some(
    (future) =>
      future.id !== undefined ||
      future.decisionEffects !== undefined ||
      future.usefulWorkIds !== undefined,
  ) ||
  portfolio.tests.some((test) =>
    test.predictions.some((prediction) => prediction.compatibleFutureIds !== undefined),
  )

function contextValidationErrors(portfolio: PortfolioStructure): string[] {
  if (!hasStructuredIdentifiers(portfolio)) {
    return ['falsification.structure.legacy-missing-identifiers']
  }
  const errors: string[] = []
  if (portfolio.criteria === undefined) errors.push('falsification.criteria.missing-catalog')
  if (portfolio.workItems === undefined) errors.push('falsification.work.missing-catalog')

  const criteria = portfolio.criteria ?? []
  const criterionIds = criteria.map((criterion) => criterion.id)
  for (const id of duplicateIds(criterionIds)) {
    errors.push(`falsification.criteria.duplicate-id:${id}`)
  }
  for (const criterion of criteria) {
    if (!criterion.id.trim()) errors.push('falsification.criteria.empty-id')
    if (!criterion.text.trim()) errors.push(`falsification.criteria.empty-text:${criterion.id}`)
  }

  const workItems = portfolio.workItems ?? []
  const workIds = workItems.map((work) => work.id)
  for (const id of duplicateIds(workIds)) errors.push(`falsification.work.duplicate-id:${id}`)
  for (const work of workItems) {
    if (!work.id.trim()) errors.push('falsification.work.empty-id')
    else if (
      !work.id.startsWith(`${portfolio.id}:work:`) ||
      work.id.length === `${portfolio.id}:work:`.length
    ) {
      errors.push(`falsification.work.unexpected-id:${work.id}`)
    }
    if (!work.label.trim()) errors.push(`falsification.work.empty-label:${work.id}`)
  }

  const expectedCriterionIds = new Set(criterionIds)
  const expectedWorkIds = new Set(workIds)
  const seenFutureIds = portfolio.futures.flatMap((future) => (future.id ? [future.id] : []))
  for (const id of duplicateIds(seenFutureIds)) {
    errors.push(`falsification.future.duplicate-id:${id}`)
  }
  for (const future of portfolio.futures) {
    const expectedId = falsificationFutureId(portfolio.id, future.bearing)
    if (future.id === undefined) errors.push(`falsification.future.missing-id:${future.bearing}`)
    else if (future.id !== expectedId) {
      errors.push(`falsification.future.unexpected-id:${future.id}:expected:${expectedId}`)
    }

    if (future.decisionEffects === undefined) {
      errors.push(`falsification.future.missing-decision-effects:${expectedId}`)
    } else {
      const effectIds = future.decisionEffects.map((effect) => effect.criterionId)
      for (const id of duplicateIds(effectIds)) {
        errors.push(`falsification.future.duplicate-criterion:${expectedId}:${id}`)
      }
      for (const id of effectIds) {
        if (!expectedCriterionIds.has(id)) {
          errors.push(`falsification.future.unknown-criterion:${expectedId}:${id}`)
        }
      }
      for (const id of criterionIds) {
        if (!effectIds.includes(id)) {
          errors.push(`falsification.future.missing-criterion:${expectedId}:${id}`)
        }
      }
    }

    if (future.usefulWorkIds === undefined) {
      errors.push(`falsification.future.missing-useful-work-ids:${expectedId}`)
    } else {
      for (const id of duplicateIds(future.usefulWorkIds)) {
        errors.push(`falsification.future.duplicate-work:${expectedId}:${id}`)
      }
      for (const id of future.usefulWorkIds) {
        if (!expectedWorkIds.has(id)) {
          errors.push(`falsification.future.unknown-work:${expectedId}:${id}`)
        }
      }
    }
  }
  return sortedUnique(errors)
}

function testValidationErrors(portfolio: PortfolioStructure, test: DiscriminatingTest): string[] {
  const errors: string[] = []
  const futureIds = portfolio.futures.flatMap((future) => (future.id ? [future.id] : []))
  const allowed = new Set(futureIds)
  const reached = new Set<string>()
  for (const prediction of test.predictions) {
    const path = `${test.id}:${prediction.bearing}`
    if (prediction.compatibleFutureIds === undefined) {
      errors.push(`falsification.prediction.missing-compatible-futures:${path}`)
      continue
    }
    if (prediction.compatibleFutureIds.length === 0) {
      errors.push(`falsification.prediction.empty-compatible-futures:${path}`)
    }
    for (const id of duplicateIds(prediction.compatibleFutureIds)) {
      errors.push(`falsification.prediction.duplicate-future:${path}:${id}`)
    }
    for (const id of prediction.compatibleFutureIds) {
      if (!allowed.has(id)) errors.push(`falsification.prediction.unknown-future:${path}:${id}`)
      else reached.add(id)
    }
  }
  for (const id of futureIds) {
    if (!reached.has(id))
      errors.push(`falsification.prediction.unreachable-future:${test.id}:${id}`)
  }
  return sortedUnique(errors)
}

/** Exact fail-closed validation used by engine parsing, reload, and recommendation. */
export function validateFalsificationStructure(portfolio: PortfolioStructure): string[] {
  const context = contextValidationErrors(portfolio)
  if (context.includes('falsification.structure.legacy-missing-identifiers')) return context
  return sortedUnique([
    ...context,
    ...portfolio.tests.flatMap((test) => testValidationErrors(portfolio, test)),
  ])
}

const chooseTwo = (value: number): number => (value * (value - 1)) / 2

const emptyDerivation = (
  testId: string,
  status: Exclude<FalsificationDerivationStatus, 'derived'>,
  rules: string[],
  futureCount: number,
): FalsificationTestDerivation => ({
  version: 1,
  testId,
  status,
  rules,
  futureCount,
  totalFuturePairs: chooseTwo(futureCount),
  outcomes: [],
  worstCaseFuturesEliminated: 0,
  meanPairEliminations: 0,
  worstCaseDecisionCriteriaResolved: 0,
  decisionCriteriaChanged: [],
  worstCaseReusableWork: 0,
  reusableWorkUniverse: 0,
  worstCaseReusableWorkRatio: 0,
})

const intersection = (sets: readonly ReadonlySet<string>[]): string[] => {
  if (!sets.length) return []
  return [...sets[0]!]
    .filter((id) => sets.slice(1).every((set) => set.has(id)))
    .sort(compareStableId)
}

function deriveTest(
  portfolio: PortfolioStructure,
  test: DiscriminatingTest,
): FalsificationTestDerivation {
  const contextErrors = contextValidationErrors(portfolio)
  if (contextErrors.includes('falsification.structure.legacy-missing-identifiers')) {
    return emptyDerivation(test.id, 'legacy-inert', contextErrors, portfolio.futures.length)
  }
  const errors = sortedUnique([...contextErrors, ...testValidationErrors(portfolio, test)])
  if (errors.length) return emptyDerivation(test.id, 'invalid', errors, portfolio.futures.length)

  const futureById = new Map(portfolio.futures.map((future) => [future.id!, future]))
  const criteria = portfolio.criteria ?? []
  const totalFuturePairs = chooseTwo(portfolio.futures.length)
  const outcomes = test.predictions.map((prediction) => {
    const compatibleFutureIds = sortedUnique(prediction.compatibleFutureIds!)
    const compatible = compatibleFutureIds.map((id) => futureById.get(id)!)
    const compatibleSet = new Set(compatibleFutureIds)
    const eliminatedFutureIds = portfolio.futures
      .map((future) => future.id!)
      .filter((id) => !compatibleSet.has(id))
      .sort(compareStableId)
    const resolvedCriteria = criteria.flatMap((criterion) => {
      const dispositions = compatible.map(
        (future) =>
          future.decisionEffects!.find((effect) => effect.criterionId === criterion.id)!
            .disposition,
      )
      const first = dispositions[0]
      return first && first !== 'unknown' && dispositions.every((value) => value === first)
        ? [{ criterionId: criterion.id, disposition: first }]
        : []
    })
    const reusableWorkIds = intersection(
      compatible.map((future) => new Set(future.usefulWorkIds ?? [])),
    )
    return {
      bearing: prediction.bearing,
      compatibleFutureIds,
      eliminatedFutureIds,
      separatedFuturePairs: totalFuturePairs - chooseTwo(compatibleFutureIds.length),
      resolvedCriteria,
      reusableWorkIds,
    }
  })
  const changed = criteria.flatMap((criterion) => {
    const dispositions = new Set(
      outcomes.flatMap((outcome) =>
        outcome.resolvedCriteria
          .filter((effect) => effect.criterionId === criterion.id)
          .map((effect) => effect.disposition),
      ),
    )
    return dispositions.size > 1 ? [criterion.id] : []
  })
  const reusableWorkUniverse = portfolio.workItems?.length ?? 0
  const worstCaseReusableWork = Math.min(
    ...outcomes.map((outcome) => outcome.reusableWorkIds.length),
  )
  return {
    version: 1,
    testId: test.id,
    status: 'derived',
    rules: [
      'falsification.derivation.compatible-future-set.v1',
      'falsification.derivation.unanimous-criterion-disposition.v1',
      'falsification.derivation.useful-work-intersection.v1',
    ],
    futureCount: portfolio.futures.length,
    totalFuturePairs,
    outcomes,
    worstCaseFuturesEliminated: Math.min(
      ...outcomes.map((outcome) => outcome.eliminatedFutureIds.length),
    ),
    meanPairEliminations:
      outcomes.reduce((sum, outcome) => sum + outcome.separatedFuturePairs, 0) / outcomes.length,
    worstCaseDecisionCriteriaResolved: Math.min(
      ...outcomes.map((outcome) => outcome.resolvedCriteria.length),
    ),
    decisionCriteriaChanged: changed,
    worstCaseReusableWork,
    reusableWorkUniverse,
    worstCaseReusableWorkRatio:
      reusableWorkUniverse === 0 ? 0 : worstCaseReusableWork / reusableWorkUniverse,
  }
}

const completeUserPrice = (test: DiscriminatingTest): boolean =>
  test.cost.source === 'user' &&
  test.cost.effortHours !== null &&
  test.cost.elapsedDays !== null &&
  test.cost.moneyUsd !== null

const benefitVector = (receipt: FalsificationTestDerivation): number[] => [
  receipt.worstCaseFuturesEliminated,
  receipt.meanPairEliminations,
  receipt.worstCaseDecisionCriteriaResolved,
  receipt.decisionCriteriaChanged.length,
  receipt.worstCaseReusableWork,
]

const costVector = (test: DiscriminatingTest): number[] => [
  test.cost.effortHours!,
  test.cost.elapsedDays!,
  test.cost.moneyUsd!,
]

/** True only for strict Pareto dominance over every named benefit and cost axis. */
export function falsificationTestDominates(
  leftTest: DiscriminatingTest,
  left: FalsificationTestDerivation,
  rightTest: DiscriminatingTest,
  right: FalsificationTestDerivation,
): boolean {
  if (
    left.status !== 'derived' ||
    right.status !== 'derived' ||
    !completeUserPrice(leftTest) ||
    !completeUserPrice(rightTest)
  ) {
    return false
  }
  const leftBenefits = benefitVector(left)
  const rightBenefits = benefitVector(right)
  const leftCosts = costVector(leftTest)
  const rightCosts = costVector(rightTest)
  const noWorse =
    leftBenefits.every((value, index) => value >= rightBenefits[index]!) &&
    leftCosts.every((value, index) => value <= rightCosts[index]!)
  const strictlyBetter =
    leftBenefits.some((value, index) => value > rightBenefits[index]!) ||
    leftCosts.some((value, index) => value < rightCosts[index]!)
  return noWorse && strictlyBetter
}

const recommendationReceipt = (
  tests: readonly DiscriminatingTest[],
  derivations: readonly FalsificationTestDerivation[],
): FalsificationRecommendationReceipt => {
  const base = {
    version: 1 as const,
    rule: 'falsification.recommendation.unique-pareto-dominator.v1' as const,
    consideredTestIds: tests.map((test) => test.id).sort(compareStableId),
    benefitAxes: [
      'worstCaseFuturesEliminated',
      'meanPairEliminations',
      'worstCaseDecisionCriteriaResolved',
      'decisionCriteriaChanged',
      'worstCaseReusableWork',
    ] as const,
    costAxes: ['effortHours', 'elapsedDays', 'moneyUsd'] as const,
  }
  if (!tests.length) return { ...base, status: 'no-tests' }
  if (derivations.some((receipt) => receipt.status === 'legacy-inert')) {
    return { ...base, status: 'legacy-inert' }
  }
  if (derivations.some((receipt) => receipt.status === 'invalid')) {
    return { ...base, status: 'invalid-structure' }
  }
  if (!tests.every(completeUserPrice)) return { ...base, status: 'needs-user-prices' }

  const receiptById = new Map(derivations.map((receipt) => [receipt.testId, receipt]))
  const winners = tests.filter((candidate) =>
    tests.every(
      (other) =>
        other.id === candidate.id ||
        falsificationTestDominates(
          candidate,
          receiptById.get(candidate.id)!,
          other,
          receiptById.get(other.id)!,
        ),
    ),
  )
  return winners.length === 1
    ? { ...base, status: 'recommended', winnerId: winners[0]!.id }
    : { ...base, status: 'no-unique-dominator' }
}

type PortfolioInput = Omit<
  FalsificationPortfolio,
  'derivations' | 'recommendation' | 'recommendedTestId' | 'resolved'
>

export function completeFalsificationPortfolio(
  base: PortfolioInput,
  _weights: PortfolioWeights = DEFAULT_PORTFOLIO_WEIGHTS,
): FalsificationPortfolio {
  const complete = completeFutures(base.futures)
  if (complete.missing.length) {
    throw new Error(`A falsification portfolio is missing: ${complete.missing.join(', ')}`)
  }
  const seenTestIds = new Set<string>()
  const tests = base.tests
    .map(normalizeTest)
    .filter(executableDiscriminatingTest)
    .filter((test) => {
      if (seenTestIds.has(test.id)) return false
      seenTestIds.add(test.id)
      return true
    })
  const structure: PortfolioStructure = { ...base, tests, futures: complete.futures }
  const derivations = tests.map((test) => deriveTest(structure, test))
  const receiptById = new Map(derivations.map((receipt) => [receipt.testId, receipt]))
  const dominanceCount = (test: DiscriminatingTest): number =>
    tests.filter(
      (other) =>
        other.id !== test.id &&
        falsificationTestDominates(
          test,
          receiptById.get(test.id)!,
          other,
          receiptById.get(other.id)!,
        ),
    ).length
  tests.sort(
    (left, right) =>
      dominanceCount(right) - dominanceCount(left) || compareStableId(left.id, right.id),
  )
  derivations.sort(
    (left, right) =>
      tests.findIndex((test) => test.id === left.testId) -
      tests.findIndex((test) => test.id === right.testId),
  )
  const recommendation = recommendationReceipt(tests, derivations)
  return {
    ...base,
    tests,
    futures: complete.futures,
    derivations,
    recommendation,
    ...(recommendation.winnerId ? { recommendedTestId: recommendation.winnerId } : {}),
  }
}

/** Re-derive the whole Pareto record after the user replaces one proposal's cost. */
export function updateFalsificationTestCost(
  portfolio: FalsificationPortfolio,
  testId: string,
  cost: UserTestCost,
  weights: PortfolioWeights = DEFAULT_PORTFOLIO_WEIGHTS,
): FalsificationPortfolio {
  if (!portfolio.tests.some((test) => test.id === testId)) {
    throw new Error(`Unknown falsification test: ${testId}`)
  }
  const reranked = completeFalsificationPortfolio(
    {
      id: portfolio.id,
      targetId: portfolio.targetId,
      targetClaim: portfolio.targetClaim,
      createdAt: portfolio.createdAt,
      ...(portfolio.opId ? { opId: portfolio.opId } : {}),
      ...(portfolio.model ? { model: portfolio.model } : {}),
      tests: portfolio.tests.map((test) =>
        test.id === testId ? withUserTestCost(test, cost) : test,
      ),
      futures: portfolio.futures,
      ...(portfolio.criteria !== undefined ? { criteria: portfolio.criteria } : {}),
      ...(portfolio.workItems !== undefined ? { workItems: portfolio.workItems } : {}),
      structuralValue: portfolio.structuralValue,
    },
    weights,
  )
  return portfolio.resolved ? { ...reranked, resolved: portfolio.resolved } : reranked
}

export const outcomeBearing = (bearing: 'supported' | 'refuted' | 'inconclusive'): FutureBearing =>
  bearing === 'supported' ? 'positive' : bearing === 'refuted' ? 'negative' : 'inconclusive'

/** Collapse the prepared futures without deleting any of them from the audit record. */
export function resolveFalsificationPortfolio(
  portfolio: FalsificationPortfolio,
  bearing: 'supported' | 'refuted' | 'inconclusive',
  outcomeRecordedAt: number,
): FalsificationPortfolio {
  const landed = outcomeBearing(bearing)
  const activeFuture = portfolio.futures.find((future) => future.bearing === landed)
  if (!activeFuture) throw new Error(`The portfolio has no ${landed} future.`)
  return {
    ...portfolio,
    resolved: {
      bearing: landed,
      outcomeRecordedAt,
      activeFuture,
      inactive: FUTURES.filter((row) => row !== landed),
    },
  }
}
